import { ApplicationContext } from '@upside/spa-shared-context';

import { appHeaderAppName, paths } from 'shell/const';
import {
  addErrorHandler,
  getAppStatus,
  registerApplication,
  LifeCycles,
  LOAD_ERROR,
  SKIP_BECAUSE_BROKEN,
} from 'single-spa';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const appHeaderError = require('../app-header-error.html');

const APP_HEADER_SELECTOR = '#app-header';
const FALLBACK_SELECTOR = '.appHeaderError';

/**
 * List of paths where the app header should not be shown
 */
const PATHS_WITHOUT_APP_HEADER = [
  `/${paths.VERIFY_MANAGED_SUBSCRIPTION}`,
  `/${paths.ASSET_SETTLEMENT}`,
];

/**
 * Whether the app header should be shown for the current authentication state and path.
 */
const shouldShowAppHeader = (
  context: ApplicationContext,
  pathname: string,
): boolean =>
  context.authentication.loggedIn &&
  !PATHS_WITHOUT_APP_HEADER.some((path) => pathname.startsWith(path));

/**
 * Registers appheader if the local storage feature flag globalheader is not set
 */
export const registerAppHeader = (context: ApplicationContext): void => {
  if (!getAppStatus(appHeaderAppName)) {
    registerApplication({
      activeWhen: ({ pathname }) => shouldShowAppHeader(context, pathname),
      app: () => System.import(appHeaderAppName) as Promise<LifeCycles>,
      name: appHeaderAppName,
      customProps: {
        context,
        domElementGetter: () => document.querySelector(APP_HEADER_SELECTOR),
      },
    });

    // The app header is registered programmatically (not via the single-spa-layout
    // template), so its error template can't be wired up declaratively. When the app is
    // broken, render the fallback into its container - but only on routes where the app
    // header should be shown, and remove it again otherwise (e.g. when navigating to a
    // global-header route such as asset settlement). This is re-evaluated on every
    // single-spa routing event so a stale fallback can't leak onto a different view.
    const renderAppHeaderFallback = (): void => {
      const container = document.querySelector(APP_HEADER_SELECTOR);

      if (!container) {
        return;
      }

      const status = getAppStatus(appHeaderAppName);
      const isBroken = status === LOAD_ERROR || status === SKIP_BECAUSE_BROKEN;
      const shouldShow =
        isBroken && shouldShowAppHeader(context, window.location.pathname);
      const hasFallback = Boolean(container.querySelector(FALLBACK_SELECTOR));

      if (shouldShow && !hasFallback) {
        container.innerHTML = appHeaderError;
      } else if (!shouldShow && hasFallback) {
        container.innerHTML = '';
      }
    };

    addErrorHandler((error) => {
      if (error.appOrParcelName === appHeaderAppName) {
        renderAppHeaderFallback();
      }
    });

    window.addEventListener(
      'single-spa:routing-event',
      renderAppHeaderFallback,
    );
  }
};
